(() => {
  'use strict';
  if (window.toneShell) return;
  document.documentElement.classList.replace('no-js', 'js');
  const instances = new Map();
  const emailPattern = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/;
  const successCookie = 'tone_newsletter_success';
  const codeKey = 'tone_newsletter_code';
  let issuedCode = '';
  const safeCode = value => typeof value === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(value.trim()) ? value.trim() : '';
  function recallCode() {
    try { return safeCode(window.localStorage.getItem(codeKey)); }
    catch { return ''; }
  }
  function rememberCode(code) {
    if (!code) return;
    try { window.localStorage.setItem(codeKey, code); }
    catch { /* The current page retains only the issued promotional code in memory. */ }
  }
  function remembered() {
    try { return document.cookie.split(';').some(value => value.trim() === `${successCookie}=true`); }
    catch { return false; }
  }
  function remember() {
    try { document.cookie = `${successCookie}=true; Path=/; Max-Age=31536000; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`; }
    catch { /* Signup remains successful when functional cookies are unavailable. */ }
  }

  function initNewsletter(root) {
    if (instances.has(root) || typeof fetch !== 'function') return;
    const form = root.querySelector('form');
    const email = form?.querySelector('[name=email]');
    const button = form?.querySelector('[type=submit]');
    const fields = root.querySelector('[data-newsletter-fields]');
    const success = root.querySelector('[data-newsletter-success]');
    const error = root.querySelector('[data-newsletter-error]');
    if (!form || !email || !button || !fields || !success || !error) return;
    const lifetime = new AbortController();
    const defaultButtonText = button.textContent;
    const description = email.getAttribute('aria-describedby');
    let pending = false;
    let request;
    const clearError = () => {
      error.hidden = true;
      error.textContent = '';
      email.removeAttribute('aria-invalid');
      if (description) email.setAttribute('aria-describedby', description);
      else email.removeAttribute('aria-describedby');
    };
    const showError = (message, invalid = false) => {
      error.textContent = message;
      error.hidden = false;
      if (invalid) {
        email.setAttribute('aria-invalid', 'true');
        email.setAttribute('aria-describedby', [description, error.id].filter(Boolean).join(' '));
        email.focus({preventScroll: true});
      }
    };
    const complete = (payload = {}, focus = false) => {
      clearError();
      const code = safeCode(payload.discountCode) || issuedCode || (root.dataset.remember === 'true' ? recallCode() : '');
      fields.hidden = Boolean(code);
      form.reset();
      const message = code ? root.dataset.code : root.dataset.recovery;
      const codeIndex = code ? message.indexOf('[code]') : -1;
      if (codeIndex >= 0) {
        const codeLabel = document.createElement('strong');
        codeLabel.className = 'tone-newsletter__code';
        codeLabel.textContent = code;
        success.replaceChildren(message.slice(0, codeIndex), codeLabel, message.slice(codeIndex + '[code]'.length));
      } else {
        success.textContent = message;
      }
      success.hidden = false;
      root.dataset.newsletterComplete = String(Boolean(code));
      if (focus) success.focus({preventScroll: true});
    };
    const localError = value => {
      if (!value) return root.dataset.required;
      const at = value.lastIndexOf('@');
      if (value.length > 254 || at > 64) return root.dataset.tooLong;
      if (at < 1 || value.includes('..') || !emailPattern.test(value)) return root.dataset.invalid;
      return null;
    };
    const serverMessage = (payload, status) => {
      if (status === 429) return root.dataset.rateLimit;
      if (typeof payload?.message !== 'string') return root.dataset.serverError;
      if (/enter your email/i.test(payload.message)) return root.dataset.required;
      if (/longer than|too long/i.test(payload.message)) return root.dataset.tooLong;
      if (/look like|invalid email|valid email|check for a typo/i.test(payload.message)) return root.dataset.invalid;
      return payload.message.slice(0, 400) || root.dataset.serverError;
    };
    form.noValidate = true;
    email.addEventListener('input', clearError, {signal: lifetime.signal});
    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (pending || root.dataset.newsletterComplete === 'true') return;
      const value = email.value.trim();
      const problem = localError(value);
      if (problem) { showError(problem, true); return; }
      pending = true;
      clearError();
      const original = button.textContent;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = root.dataset.submitting;
      request = new AbortController();
      const timeout = setTimeout(() => request.abort(), 20000);
      const body = new URLSearchParams();
      body.set('email', value);
      body.set('source', form.elements.source.value);
      body.set('website', form.elements.website.value || '');
      try {
        const response = await fetch(form.action, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
          body: body.toString(),
          signal: request.signal
        });
        const isJSON = (response.headers.get('content-type') || '').includes('application/json');
        let payload = null;
        if (isJSON) {
          try { payload = await response.json(); }
          catch {
            if (!lifetime.signal.aborted) showError(root.dataset.serverError);
            return;
          }
        }
        if (lifetime.signal.aborted) return;
        if (response.ok && payload?.ok === true) {
          issuedCode = safeCode(payload.discountCode) || issuedCode;
          if (root.dataset.remember === 'true') {
            remember();
            rememberCode(issuedCode);
          }
          complete(payload, true);
          document.dispatchEvent(new CustomEvent('tone:newsletter-success'));
        } else {
          showError(serverMessage(payload, response.status), response.status === 400 || response.status === 422);
        }
      } catch {
        if (!lifetime.signal.aborted) showError(root.dataset.networkError);
      } finally {
        clearTimeout(timeout);
        pending = false;
        if (!lifetime.signal.aborted) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.textContent = original;
        }
      }
    }, {signal: lifetime.signal});
    document.addEventListener('tone:newsletter-success', () => {
      if (root.dataset.newsletterComplete !== 'true' && !pending) complete();
    }, {signal: lifetime.signal});
    if (root.dataset.remember === 'true' && (remembered() || recallCode())) complete();
    instances.set(root, () => {
      lifetime.abort();
      request?.abort();
      form.noValidate = false;
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.textContent = defaultButtonText;
    });
  }

  function initHeader(root) {
    if (instances.has(root)) return;
    const lifetime = new AbortController();
    const nav = root.querySelector('.hdr__nav');
    const inner = root.querySelector('.hdr__in');
    const mobile = root.querySelector('.hdr__mobile');
    const desktop = matchMedia('(min-width: 961px)');
    let frame;
    function close(details, focus = false) {
      details.open = false;
      if (focus) details.querySelector('summary')?.focus({preventScroll: true});
    }
    function measure() {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        root.classList.remove('is-compact');
        if (desktop.matches && inner && nav) {
          const style = getComputedStyle(inner);
          const available = inner.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
          const children = [...inner.children];
          const required = children.reduce((total, child) => total + Math.max(child.scrollWidth, child.getBoundingClientRect().width), 0) + parseFloat(style.columnGap || style.gap || 0) * (children.length - 1) + 6;
          if (required > available + 1) root.classList.add('is-compact');
          else if (mobile?.open) close(mobile);
        }
        if (inner) {
          const top = parseFloat(getComputedStyle(root).top) || 0;
          document.documentElement.style.setProperty('--header-h', `${Math.max(0, root.offsetHeight + top - 6)}px`);
        }
      });
    }
    root.querySelectorAll('[data-tone-disclosure]').forEach(details => {
      const summary = details.querySelector('summary');
      if (!summary) return;
      summary.setAttribute('aria-expanded', String(details.open));
      details.addEventListener('toggle', () => summary.setAttribute('aria-expanded', String(details.open)), {signal: lifetime.signal});
    });
    document.addEventListener('click', event => {
      root.querySelectorAll('[data-tone-disclosure][open]').forEach(details => {
        if (!details.contains(event.target)) close(details);
      });
    }, {signal: lifetime.signal});
    document.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      const open = [...root.querySelectorAll('[data-tone-disclosure][open]')];
      const active = open.reverse().find(details => details.contains(document.activeElement)) || open[0];
      if (active) { event.preventDefault(); close(active, true); }
    }, {signal: lifetime.signal});
    root.addEventListener('click', event => {
      if (event.target.closest('a[href]') && mobile?.open) close(mobile);
    }, {signal: lifetime.signal});
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    resize?.observe(root);
    window.addEventListener('resize', measure, {passive: true, signal: lifetime.signal});
    document.fonts?.ready.then(() => { if (!lifetime.signal.aborted) measure(); });
    measure();
    instances.set(root, () => { lifetime.abort(); resize?.disconnect(); cancelAnimationFrame(frame); });
  }
  function boot(root = document) {
    if (root.matches?.('[data-tone-header]')) initHeader(root);
    if (root.matches?.('[data-tone-newsletter]')) initNewsletter(root);
    root.querySelectorAll?.('[data-tone-header]').forEach(initHeader);
    root.querySelectorAll?.('[data-tone-newsletter]').forEach(initNewsletter);
  }
  function collect() {
    for (const [root, destroy] of instances) if (!root.isConnected) { destroy(); instances.delete(root); }
  }
  document.addEventListener('shopify:section:load', event => boot(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, destroy] of instances) if (event.target.contains(root)) { destroy(); instances.delete(root); }
  });
  const observer = new MutationObserver(records => {
    for (const record of records) for (const node of record.addedNodes) if (node.nodeType === 1) boot(node);
    collect();
  });
  boot();
  observer.observe(document.body, {childList: true, subtree: true});
  window.toneShell = {init: boot};
  window.addEventListener('pagehide', () => {
    observer.disconnect();
    for (const destroy of instances.values()) destroy();
    instances.clear();
  });
  window.addEventListener('pageshow', () => {
    boot();
    observer.observe(document.body, {childList: true, subtree: true});
  });
})();
